import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Layout, Row, Col, Card, message, Drawer, Button, Tree, Input } from 'antd';
import { CloseOutlined, FilterFilled, FilterOutlined } from '@ant-design/icons';
import { ExportToCsv } from 'export-to-csv';
import { CSV_EXPORT_DEFAULTS } from '../../utils/DataConstants';
import { getObjectValues, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { getContactName } from '../../utils/DataFormatterUtils';
import Ellipsis from 'ant-design-pro/lib/Ellipsis';
import {
	addSupplierDispatcherCCs,
	dispatchersRestCrudThunksForSupplier,
	dispatchersRestCrudThunksForSupplierBulk,
	removeSupplierDispatcherCCs,
} from '../../thunks/dispatchers_thunks';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import TableWrapperComponent from '../table_wrapper_component/TableWrapperComponent';
import { fetchFacilityActiveUsers } from '../../thunks/users_thunks';
import { supplierContactsRestCrudThunksForSupplier } from '../../thunks/supplier_contacts_thunks';
import { fieldTechsRestCrudThunksForSupplier } from '../../thunks/field_techs_thunks';
import defaultTablePagination from '../pagination_component/PaginationComponent';
import { UserName } from '../name_component/user/UserNameComponent';
import { UserNameList } from '../name_component/user/UserNameList';
import LocationPageDisplay from '../location_page_display/LocationPageDisplay';
import {
	fetchLocationsWithBuyerCompaniesForSupplier,
	fetchLocationsWithRegionsAndSubRegionsForSupplier,
} from '../../thunks/locations_thunks';
import { isInternalTech } from '../../utils/AuthUtils';
import useMountEffect from '../../hooks/useMountEffect';

const { Content } = Layout;

const ROUTING_LOCATION_FILTER_COLLECTION = 'routingPageLocationFilter';

const buildLocationTreeData = (entities: any[]): any[] => {
	if (!Array.isArray(entities)) return [];
	return entities.reduce((acc, entity) => {
		if (entity.isActive === false) return acc;
		const hasChildren = Array.isArray(entity.children) && entity.children.length > 0;
		const children = hasChildren ? buildLocationTreeData(entity.children) : undefined;
		if (hasChildren && (!children || children.length === 0)) return acc;
		acc.push({
			key: nullSafeGet('id', entity),
			title: nullSafeGetOrElse('name', entity, ''),
			children,
		});
		return acc;
	}, []);
};

const filterTreeBySearch = (nodes: any[], lcSearch: string): any[] =>
	nodes.reduce((acc, node) => {
		const titleString = typeof node.title === 'string' ? node.title : String(node.title || '');
		const titleMatch = titleString.toLowerCase().includes(lcSearch);
		const filteredChildren =
			Array.isArray(node.children) && node.children.length > 0
				? filterTreeBySearch(node.children, lcSearch)
				: [];
		if (titleMatch || filteredChildren.length > 0) {
			acc.push({
				...node,
				children: filteredChildren.length > 0 ? filteredChildren : undefined,
			});
		}
		return acc;
	}, []);

const RoutingLocationFilterTreeBase: React.FC<any> = ({
	currentUser,
	locations,
	fetchRegionHierarchy,
	fetchBuyerCompanies,
	checkedKeys,
	onChange,
	searchText,
}) => {
	useMountEffect(() => {
		const pagination = { current: 1, pageSize: 1000 };
		const sorting = { sort_by: 'name', order: 'ascend' };
		if (isInternalTech(currentUser)) {
			fetchRegionHierarchy(
				{},
				ROUTING_LOCATION_FILTER_COLLECTION,
				pagination,
				sorting,
				null,
				false
			);
		} else {
			fetchBuyerCompanies({}, ROUTING_LOCATION_FILTER_COLLECTION, pagination, sorting, null, false);
		}
	});

	const recordsIds = nullSafeGetOrElse(
		`${ROUTING_LOCATION_FILTER_COLLECTION}.recordsIds`,
		locations,
		[]
	);
	const records = nullSafeGetOrElse('records', locations, {});
	const topLevel = recordsIds.map((id) => records[id]).filter(Boolean);
	const treeData = React.useMemo(() => buildLocationTreeData(topLevel), [topLevel]);
	const filteredTreeData = React.useMemo(() => {
		const trimmed = (searchText || '').trim().toLowerCase();
		if (!trimmed) return treeData;
		return filterTreeBySearch(treeData, trimmed);
	}, [treeData, searchText]);

	if (treeData.length === 0) {
		return <div style={{ padding: 16, color: 'rgba(0,0,0,0.45)' }}>Loading locations...</div>;
	}

	if (filteredTreeData.length === 0) {
		return <div style={{ padding: 16, color: 'rgba(0,0,0,0.45)' }}>No matching locations</div>;
	}

	const toggleNodeCheck = (clickedNode: any) => {
		const key = nullSafeGet('key', clickedNode);
		const subtreeKeys: any[] = [];
		const collect = (n: any) => {
			subtreeKeys.push(n.key);
			if (Array.isArray(n.children) && n.children.length > 0) {
				n.children.forEach(collect);
			}
		};
		collect(clickedNode);

		const currentKeys = Array.isArray(checkedKeys) ? checkedKeys : [];
		const isCurrentlyChecked = currentKeys.includes(key);

		let nextKeys: any[];
		if (isCurrentlyChecked) {
			const removeSet = new Set(subtreeKeys);
			nextKeys = currentKeys.filter((existing: any) => !removeSet.has(existing));
		} else {
			nextKeys = Array.from(new Set([...currentKeys, ...subtreeKeys]));
		}

		onChange(nextKeys);
	};

	return (
		<Tree
			key={`${searchText || ''}-${filteredTreeData.length}`}
			checkable
			defaultExpandAll
			checkedKeys={checkedKeys}
			onCheck={(checked) => onChange(checked)}
			selectedKeys={[]}
			onSelect={(_, info) => toggleNodeCheck(info.node)}
			treeData={filteredTreeData}
		/>
	);
};

const RoutingLocationFilterTree = connect(
	(state: any) => ({
		currentUser: state.session.currentUser,
		locations: state.locations,
	}),
	(dispatch) => ({
		fetchRegionHierarchy: (
			params,
			targetCollectionName,
			pagination,
			sorting,
			filters,
			addToTargetCollection
		) =>
			dispatch(
				fetchLocationsWithRegionsAndSubRegionsForSupplier(
					params,
					targetCollectionName,
					pagination,
					sorting,
					filters,
					addToTargetCollection
				)
			),
		fetchBuyerCompanies: (
			params,
			targetCollectionName,
			pagination,
			sorting,
			filters,
			addToTargetCollection
		) =>
			dispatch(
				fetchLocationsWithBuyerCompaniesForSupplier(
					params,
					targetCollectionName,
					pagination,
					sorting,
					filters,
					addToTargetCollection
				)
			),
	})
)(RoutingLocationFilterTreeBase);

const LocationFilterDropdownContent: React.FC<{
	initialIds: any;
	onApply: (ids: any) => void;
}> = ({ initialIds, onApply }) => {
	const [pending, setPending] = React.useState<any>(initialIds || []);
	const [searchText, setSearchText] = React.useState<string>('');
	React.useEffect(() => {
		setPending(initialIds || []);
	}, [initialIds]);
	return (
		<div
			style={{ width: 320 }}
			onMouseDown={(event) => {
				event.stopPropagation();
			}}
		>
			<div style={{ padding: 8 }}>
				<Input
					placeholder="Search..."
					value={searchText}
					onChange={(event) => setSearchText(event.target.value)}
					allowClear
				/>
			</div>
			<div style={{ maxHeight: 280, overflowY: 'auto', padding: '0 8px 8px' }}>
				<RoutingLocationFilterTree
					checkedKeys={pending}
					onChange={(keys) => setPending(keys)}
					searchText={searchText}
				/>
			</div>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					borderTop: '1px solid rgb(240,240,240)',
					padding: '7px 8px',
				}}
			>
				<Button
					size="small"
					onClick={() => {
						setPending([]);
						onApply(undefined);
					}}
				>
					Reset
				</Button>
				<Button
					size="small"
					type="primary"
					onClick={() => onApply(pending && pending.length > 0 ? pending : undefined)}
				>
					Apply
				</Button>
			</div>
		</div>
	);
};

require('./SupplierClientsIndexPage.less');
let self;

class SupplierClientsIndexPage extends React.Component<any, any> {
	formRefs: any;
	constructor(props) {
		super(props);
		self = this;
		let defaultPagination = new defaultTablePagination(self);
		this.state = {
			supplierLocationDetail: null,
			initialLoading: true,
			selectedRowKeys: [],
			currentRecord: null,
			showLocation: false,
			pagination: defaultPagination,
			selectedLocationIds: undefined,
		};
	}
	componentDidMount() {
		this.fetchDispatchers();
	}
	fetchDispatchers = () => {
		const { fetchLocationDispatchers } = this.props;
		return fetchLocationDispatchers({})
			.then(() => this.setState({ initialLoading: false }))
			.catch(() => this.setState({ initialLoading: false }));
	};
	handleLocationFilterChange = (value) => {
		const ids = Array.isArray(value) && value.length > 0 ? value.map(Number) : undefined;
		this.setState({ selectedLocationIds: ids });
	};
	updateType(type) {
		switch (type) {
			case 'add':
				return this.handleAdd;
			case 'remove':
				return this.handleRemove;
			case 'update':
				return this.handleUpdate;
		}
	}
	handleAdd(field, values, records, aself) {
		const { updateLocationDispatcher } = self.props;

		const addKey = field === 'primaryTechList' ? 'addPrimaryTechList' : 'addCcList';
		const locationDispatchers = {
			locationIds: [],
			[addKey]: values,
		};
		records.forEach((record) => {
			locationDispatchers.locationIds.push(record.locationId);
		});

		message.open({
			key: 'loadingMessage',
			type: 'loading',
			duration: 60,
			content: `${records.length} Record${records.length > 1 ? 's' : ''} Updating...`,
		});
		updateLocationDispatcher(locationDispatchers)
			.then(() => {
				message.success(`${records.length} Record${records.length > 1 ? 's' : ''} Updated`);
				self.fetchDispatchers();
				aself.props.hideModal();
			})
			.catch(() => {
				message.error(`Failed to update ${records.length} record${records.length > 1 ? 's' : ''}`);
			})
			.finally(() => {
				aself.setState({ buttonLoading: false });
				message.destroy('loadingMessage');
			});
	}
	handleRemove(field, values, records, aself) {
		const { updateLocationDispatcher } = self.props;

		const removeKey = field === 'primaryTechList' ? 'removePrimaryTechList' : 'removeCcList';
		const locationDispatchers = {
			locationIds: [],
			[removeKey]: values,
		};
		records.forEach((record) => {
			locationDispatchers.locationIds.push(record.locationId);
		});

		message.open({
			key: 'loadingMessage',
			type: 'loading',
			duration: 60,
			content: `${records.length} Record${records.length > 1 ? 's' : ''} Updating...`,
		});

		updateLocationDispatcher(locationDispatchers)
			.then(() => {
				message.success(`${records.length} Record${records.length > 1 ? 's' : ''} Updated`);
				self.fetchDispatchers();
				aself.props.hideModal();
			})
			.catch(() => {
				message.error(`Failed to update ${records.length} record${records.length > 1 ? 's' : ''}`);
			})
			.finally(() => {
				aself.setState({ buttonLoading: false });
				message.destroy('loadingMessage');
			});
	}
	handleUpdate(field, values, records, aself) {
		const { updateLocationDispatcher, currentUser } = self.props;
		const locationDispatchers = {
			locationIds: [],
		};
		if (field === 'primaryTechList') {
			const newValue = Array.isArray(values) ? values : values ? [values] : [];
			const existingEmails = records.reduce(function (acc, record) {
				var contacts = nullSafeGetOrElse('primaryTechListContacts', record, []);
				contacts.forEach(function (c) {
					var email = nullSafeGet('email', c);
					if (email && acc.indexOf(email) === -1) {
						acc.push(email);
					}
				});
				return acc;
			}, []);
			locationDispatchers['removePrimaryTechList'] = existingEmails;
			locationDispatchers['addPrimaryTechList'] = newValue;
		} else {
			locationDispatchers[field] = values;
		}
		records.forEach((record) => {
			locationDispatchers.locationIds.push(record.locationId);
		});
		message.open({
			key: 'loadingMessage',
			type: 'loading',
			duration: 60,
			content: `${records.length} Record${records.length > 1 ? 's' : ''} Updating...`,
		});
		updateLocationDispatcher(locationDispatchers)
			.then(() => {
				this.setState({ locationDispatcherUpdateVisible: false });
				message.success(`${records.length} Record${records.length > 1 ? 's' : ''} Updated`);
				self.fetchDispatchers();
				aself.props.hideModal();
			})
			.catch(() => {
				message.error(`Failed to update ${records.length} record${records.length > 1 ? 's' : ''}`);
			})
			.finally(() => {
				aself.setState({ buttonLoading: false });
				message.destroy('loadingMessage');
			});
	}

	onSelectChange = (newSelectedRowKeys) => {
		this.setSelectedRowKeys(newSelectedRowKeys);
	};
	setSelectedRowKeys = (newSelectedRowKeys) => {
		this.setState({ selectedRowKeys: newSelectedRowKeys });
	};
	showLocation = (record) => {
		return {
			onClick: (event) => {
				this.setState({ currentRecord: record, showLocation: true });
			},
		};
	};
	hideLocation = (event) => {
		this.setState({ showLocation: false });
	};

	exportCSV = (records) => {
		if (!records || records.length === 0) {
			message.info('No records to export');
			return;
		}
		const formatContacts = (contacts) =>
			(contacts || [])
				.map((c) => getContactName(c).trim())
				.filter(Boolean)
				.join('; ');

		const csvData = records.map((record) => ({
			Company: nullSafeGetOrElse(
				'location.buyerCompany.displayName',
				record,
				nullSafeGetOrElse('location.buyerCompany.name', record, '')
			),
			Location: nullSafeGetOrElse('location.name', record, ''),
			'Customer Contact': formatContacts(nullSafeGet('location.facilityInChargeContacts', record)),
			Dispatcher: record.dispatcherContact
				? getContactName(record.dispatcherContact).trim()
				: nullSafeGetOrElse('dispatcher', record, ''),
			'Primary Technicians': formatContacts(nullSafeGet('primaryTechListContacts', record)),
			CCs: formatContacts(nullSafeGet('ccListContacts', record)),
		}));

		const csvExporter = new ExportToCsv({
			...CSV_EXPORT_DEFAULTS,
			filename: 'routing',
			headers: [
				'Company',
				'Location',
				'Customer Contact',
				'Dispatcher',
				'Primary Technicians',
				'CCs',
			],
		});
		csvExporter.generateCsv(csvData);
	};

	render() {
		const { dispatchers, companyConfig } = this.props;
		const { selectedRowKeys } = this.state;
		const allowMultiplePrimaryTechs = nullSafeGetOrElse(
			'config.routingConfig.allowMultiplePrimaryTechniciansForRouting',
			companyConfig,
			false
		);
		const { selectedLocationIds } = this.state;
		let privateNetworkLocations = getObjectValues(dispatchers.records).map((record) => {
			return { ...record };
		});
		if (selectedLocationIds && selectedLocationIds.length > 0) {
			const idSet = new Set(selectedLocationIds);
			privateNetworkLocations = privateNetworkLocations.filter((record) =>
				idSet.has(nullSafeGet('location.id', record))
			);
		}
		const columns = [
			{
				title: 'Company',
				dataIndex: 'displayName',
				render: (text, record) => (
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<Ellipsis tooltip={true} length={64}>
							{nullSafeGetOrElse(
								'location.buyerCompany.displayName',
								record,
								nullSafeGet('location.buyerCompany.name', record)
							)}
						</Ellipsis>
					</div>
				),
				props: {
					useDefault: true,
				},
			},
			{
				title: 'Location',
				dataIndex: 'name',
				render: (text, record) => (
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<Ellipsis tooltip={true} length={64}>
							{nullSafeGet('location.name', record)}
						</Ellipsis>
					</div>
				),
				filterDropdown: ({ confirm }) => (
					<LocationFilterDropdownContent
						initialIds={this.state.selectedLocationIds}
						onApply={(ids) => {
							this.handleLocationFilterChange(ids);
							confirm && confirm({ closeDropdown: true });
						}}
					/>
				),
				filterIcon: () =>
					this.state.selectedLocationIds && this.state.selectedLocationIds.length > 0 ? (
						<FilterFilled />
					) : (
						<FilterOutlined />
					),
				props: {
					useDefault: true,
					customFilterDropdown: true,
				},
			},
			{
				title: 'Customer Contact',
				dataIndex: 'facilityInCharge',
				render: (text, record) =>
					nullSafeGetOrElse('location.facilityInChargeContacts', record, []).length > 0 ? (
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								marginTop: -12,
								marginBottom: -12,
								gap: 8,
							}}
						>
							<UserNameList
								title={'Customer Contacts'}
								contacts={record.location.facilityInChargeContacts}
								mode={'card'}
								userType={'supplier'}
								underline={false}
							/>
						</div>
					) : (
						<div style={{ display: 'flex', alignItems: 'center' }}>{'--'}</div>
					),
				props: {
					useDefault: true,
				},
				filterSearch: true,
			},
			{
				title: 'Dispatcher',
				dataIndex: 'dispatcher',
				render: (text, record) =>
					record.dispatcherContact ? (
						<div style={{ marginTop: -12, marginBottom: -12 }}>
							<UserName
								contact={nullSafeGet('dispatcherContact', record)}
								mode={'card'}
								userType={'supplier'}
								underline={false}
							/>
						</div>
					) : record.dispatcher ? (
						<Ellipsis tooltip={true} fullWidthRecognition={true} lines={1}>
							{record.dispatcher}
						</Ellipsis>
					) : (
						'--'
					),
				props: {
					useDefault: true,
					update: {
						canUpdate: true,
						updateMethod: (type) => this.updateType(type),
						stateSlice: this.props.supplierContacts,
						fetchData: this.props.fetchSupplierContacts,
						fetchMultiple: this.props.fetchMultipleSupplierContacts,
						targetCollectionName: 'dispatcherSupplierContacts',
						type: 'single',
					},
				},
			},
			{
				title: allowMultiplePrimaryTechs ? 'Primary Tech Candidates' : 'Primary Technician',
				dataIndex: 'primaryTechList',
				render: (text, record) =>
					nullSafeGetOrElse('primaryTechListContacts', record, []).length > 0 ? (
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								marginTop: -12,
								marginBottom: -12,
								gap: 8,
							}}
						>
							<UserNameList
								title={'Primary Technicians'}
								contacts={record.primaryTechListContacts}
								mode={'card'}
								userType={'supplier'}
								underline={false}
							/>
						</div>
					) : (
						<div style={{ display: 'flex', alignItems: 'center' }}>{'--'}</div>
					),

				props: {
					useDefault: true,
					update: {
						canUpdate: true,
						updateMethod: (type) => this.updateType(type),
						stateSlice: this.props.fieldTechs,
						fetchData: this.props.fetchFieldTechs,
						fetchMultiple: this.props.fetchMultipleFieldTechs,
						targetCollectionName: 'primaryTechForLocation',
						type: allowMultiplePrimaryTechs ? 'multiple' : 'single',
					},
				},
			},
			{
				title: 'CCs',
				dataIndex: 'ccList',
				render: (text, record) =>
					nullSafeGetOrElse('ccListContacts', record, []).length > 0 ? (
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								marginTop: -12,
								marginBottom: -12,
								gap: 8,
							}}
						>
							<UserNameList
								title={'CCs'}
								contacts={record.ccListContacts}
								mode={'card'}
								userType={'supplier'}
								underline={false}
							/>
						</div>
					) : (
						<div style={{ display: 'flex', alignItems: 'center' }}>{'--'}</div>
					),

				props: {
					useDefault: true,
					update: {
						canUpdate: true,
						updateMethod: (type) => this.updateType(type),
						stateSlice: this.props.supplierContacts,
						fetchData: this.props.fetchSupplierContacts,
						fetchMultiple: this.props.fetchMultipleSupplierContacts,
						targetCollectionName: 'ccListSupplierContacts',
						type: 'multiple',
					},
				},
			},
		];

		return (
			<Content className="yourClientsPage" style={{ padding: '0 0.5em' }}>
				{/*<ScrollToTopOnMount/>*/}
				{/*<BackTop/>*/}
				{this.state.initialLoading ? (
					<PageLoadingPlaceholder />
				) : (
					<div>
						<Row style={{ margin: '0.5em -8px' }} gutter={16}>
							<Col span={24}>
								<Card bodyStyle={{ padding: 8 }}>
									<TableWrapperComponent
										columns={columns}
										showHeader={true}
										selectedRows={selectedRowKeys}
										loading={dispatchers.loading}
										dataSource={privateNetworkLocations}
										onSelectChange={this.onSelectChange}
										currentUser={this.props.currentUser}
										pagination={this.state.pagination}
										onRow={this.showLocation}
										onExport={this.exportCSV}
									/>
								</Card>
							</Col>
						</Row>
						<Drawer
							width={1000}
							placement="right"
							closable={false}
							onClose={this.hideLocation}
							visible={this.state.showLocation}
							bodyStyle={{ paddingBottom: 0 }}
						>
							<div
								//Header
								className={'absolute right-0 top-0 z-40 flex flex-row'}
							>
								<Button onClick={this.hideLocation} type={'text'} style={{ padding: '8px' }}>
									<CloseOutlined style={{ fontSize: '24px', color: 'white' }} />
								</Button>
							</div>
							<LocationPageDisplay
								record={nullSafeGet('location', this.state.currentRecord)}
								locationFetching={false}
							/>
						</Drawer>
					</div>
				)}
			</Content>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	currentUser: state.session.currentUser,
	companyConfig: state.company_config.detail,
	supplierContacts: state.supplier_contacts,
	creating: state.supplier_contacts.creating,
	dispatchers: state.dispatchers,
	updating: state.supplier_contacts.updating,
	fieldTechs: state.field_techs,
	supplierUsers: state.supplier_users,
	privateNetworkBuyerCompany: state.supplier_private_networks,
});

const mapDispatchToProps = (dispatch) => ({
	fetchLocationDispatchers: (params) => dispatch(dispatchersRestCrudThunksForSupplier.read(params)),
	updateLocationDispatcher: (entity) =>
		dispatch(dispatchersRestCrudThunksForSupplierBulk.update(entity)),
	addDispatcherCCs: (ids, entity) => dispatch(addSupplierDispatcherCCs(ids, entity)),
	removeDispatcherCCs: (ids, entity) => dispatch(removeSupplierDispatcherCCs(ids, entity)),
	fetchSupplierUsers: () => dispatch(fetchFacilityActiveUsers('supplier')()),
	fetchSupplierContacts: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			supplierContactsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleSupplierContacts: (ids, targetCollectionName) =>
		dispatch(supplierContactsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchFieldTechs: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			fieldTechsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleFieldTechs: (ids, targetCollectionName) =>
		dispatch(fieldTechsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(SupplierClientsIndexPage)
);
